import { getAllQuestionnaireAnswers } from "@medplum/core";
import { Encounter, QuestionnaireResponse } from "@medplum/fhirtypes";

import { Group, Paper, Stack, Title } from "@mantine/core";

import { TextWrapper } from "@/components/TextWrapper";
import { WordsDiff } from "@/components/WordsDiff";
import EncounterServiceDateDiffFields from "@/encounters/components/EncounterServiceDateDiffFields/EncounterServiceDateDiffFields";
import { PostpartumEncounterQuestionsLinkIds } from "@/encounters/constants/postpartum-encounter";
import { resolveEncounterInfoFields } from "@/encounters/utils/encounter-detail-version-diff";
import { getEncounterServiceDateDiffContext } from "@/encounters/utils/encounter-service-date-diff";
import { stripHtmlTags } from "@/utils/regex";

export interface PostpartumEncounterDiffProps {
  currentVersion?: QuestionnaireResponse;
  previousVersion?: QuestionnaireResponse;
  currentEncounter?: Encounter;
  previousEncounter?: Encounter;
  section?: "subjective" | "objective" | "dx-plan";
}

const PostpartumEncounterDiff = ({
  currentVersion,
  previousVersion,
  currentEncounter,
  previousEncounter,
  section,
}: PostpartumEncounterDiffProps) => {
  if (!currentVersion) return null;

  const currentAnswers = getAllQuestionnaireAnswers(currentVersion);
  const previousAnswers = previousVersion ? getAllQuestionnaireAnswers(previousVersion) : {};

  const {
    currentTemplate,
    previousTemplate,
    currentType,
    previousType,
    currentLocation,
    previousLocation,
    serviceDateDiff,
  } = resolveEncounterInfoFields(currentEncounter, previousEncounter, () => ({
    currentTemplate:
      currentAnswers[PostpartumEncounterQuestionsLinkIds.EncounterTemplate]?.[0]?.valueCoding
        ?.display,
    previousTemplate:
      previousAnswers?.[PostpartumEncounterQuestionsLinkIds.EncounterTemplate]?.[0]?.valueCoding
        ?.display,
    currentType:
      currentAnswers[PostpartumEncounterQuestionsLinkIds.EncounterType]?.[0]?.valueCoding?.display,
    previousType:
      previousAnswers?.[PostpartumEncounterQuestionsLinkIds.EncounterType]?.[0]?.valueCoding
        ?.display,
    currentLocation:
      currentAnswers[PostpartumEncounterQuestionsLinkIds.EncounterLocation]?.[0]?.valueCoding
        ?.display,
    previousLocation:
      previousAnswers?.[PostpartumEncounterQuestionsLinkIds.EncounterLocation]?.[0]?.valueCoding
        ?.display,
    serviceDateDiff: getEncounterServiceDateDiffContext({
      currentAnswers,
      previousAnswers,
    }),
  }));

  // Postpartum encounter specific fields (always sourced from the questionnaire answers)
  const currentEncounterNote = stripHtmlTags(
    currentAnswers[PostpartumEncounterQuestionsLinkIds.EncounterNote]?.[0]?.valueString,
  );
  const previousEncounterNote = stripHtmlTags(
    previousAnswers[PostpartumEncounterQuestionsLinkIds.EncounterNote]?.[0]?.valueString,
  );
  const currentClientSummary = currentAnswers["encounterClientSummary"]?.[0]?.valueString;
  const previousClientSummary = previousAnswers["encounterClientSummary"]?.[0]?.valueString;

  // Determine what content to show based on section
  const getSectionTitle = () => {
    switch (section) {
      case "subjective":
        return "Subjective Clinical Data";
      case "objective":
        return "Objective Clinical Data";
      case "dx-plan":
        return "Diagnosis & Plan";
      default:
        return "Encounter Information";
    }
  };

  const hasRelevantData = () => {
    switch (section) {
      case "subjective":
        return currentEncounterNote || previousEncounterNote;
      case "objective":
        return true; // Always show encounter info for objective
      case "dx-plan":
        return currentClientSummary || previousClientSummary;
      default:
        return true; // Always show encounter info
    }
  };

  if (!hasRelevantData()) {
    return null;
  }

  const renderDiffField = (
    label: string,
    previous: string | undefined,
    current: string | undefined,
  ) => (
    <Stack gap="0">
      <TextWrapper fw={600}>{label}</TextWrapper>
      <WordsDiff showRemoved current={current} previous={previous} />
    </Stack>
  );

  const renderDiffFieldAmended = (
    label: string,
    previous: string | undefined,
    current: string | undefined,
  ) => (
    <Stack gap="0">
      <TextWrapper fw={600}>{label}</TextWrapper>
      <WordsDiff current={current} previous={previous} />
    </Stack>
  );

  return (
    <Paper p="md" withBorder>
      <Stack mb="lg">
        <Title order={2}>{getSectionTitle()}</Title>
      </Stack>
      <Group grow align="flex-start">
        <Paper p="md" withBorder>
          <Stack>
            <Title mb="lg" order={4} c="gray.6">
              Previous
            </Title>
            {/* Always show encounter information */}
            {renderDiffField("Template", previousTemplate, currentTemplate)}
            {renderDiffField("Type", previousType, currentType)}
            {renderDiffField("Location", previousLocation, currentLocation)}
            {serviceDateDiff && (
              <EncounterServiceDateDiffFields variant="previous" diff={serviceDateDiff} />
            )}
            {/* Section-specific content */}
            {section === "subjective" && (
              <Stack gap="0">
                <TextWrapper fw={600}>Encounter Note</TextWrapper>
                <WordsDiff
                  showRemoved
                  current={currentEncounterNote}
                  previous={previousEncounterNote}
                />
              </Stack>
            )}

            {section === "dx-plan" && (
              <Stack gap="0">
                <TextWrapper fw={600}>Client Summary & Instructions</TextWrapper>
                <WordsDiff
                  showRemoved
                  current={currentClientSummary}
                  previous={previousClientSummary}
                />
              </Stack>
            )}
          </Stack>
        </Paper>
        <Paper p="md" withBorder>
          <Stack>
            <Title mb="lg" order={4} c="gray.6">
              Amended
            </Title>
            {/* Always show encounter information */}
            {renderDiffFieldAmended("Template", previousTemplate, currentTemplate)}
            {renderDiffFieldAmended("Type", previousType, currentType)}
            {renderDiffFieldAmended("Location", previousLocation, currentLocation)}
            {serviceDateDiff && (
              <EncounterServiceDateDiffFields variant="amended" diff={serviceDateDiff} />
            )}
            {/* Section-specific content */}
            {section === "subjective" && (
              <Stack gap="0">
                <TextWrapper fw={600}>Encounter Note</TextWrapper>
                <WordsDiff current={currentEncounterNote} previous={previousEncounterNote} />
              </Stack>
            )}

            {section === "dx-plan" && (
              <Stack gap="0">
                <TextWrapper fw={600}>Client Summary & Instructions</TextWrapper>
                <WordsDiff current={currentClientSummary} previous={previousClientSummary} />
              </Stack>
            )}
          </Stack>
        </Paper>
      </Group>
    </Paper>
  );
};

export default PostpartumEncounterDiff;
